import {User, UserType} from '../../../services/api.client';
import {endOf, startOf} from '../../../utilities/date-util';

type ReportDate = Date | string | null | undefined;

export interface CompanyLock {
    /** True when the Company filter is pinned to the current user's company (no picker). */
    locked: boolean;
    companyId: string | null;
    companyName: string;
}

/**
 * Bug 24463 — on screens that opt in, a Fund Admin may only report on their assigned company, so the
 * Company filter is pinned to it. Every other role keeps the company picker. The API enforces the same
 * rule server-side; this only keeps the UI honest.
 */
export function resolveCompanyLock(user: User | null | undefined, lockCompanyToFundAdmin: boolean,
                                   showCompany: boolean): CompanyLock {
    const locked = !!lockCompanyToFundAdmin && !!showCompany && user?.userType === UserType.FundAdmin;

    return {
        locked,
        companyId: locked ? user?.companyId ?? null : null,
        companyName: locked ? user?.company?.name ?? '' : '',
    };
}

/** Widens the picked dates to whole days. Returns a copy so the form controls are left untouched. */
export function normalizeReportDateRange<T extends { startDate?: ReportDate; endDate?: ReportDate; entryDate?: ReportDate }>(model: T): T {
    const normalized = {...model};
    if (normalized.startDate) {
        normalized.startDate = startOf(normalized.startDate, 'day');
    }
    if (normalized.endDate) {
        normalized.endDate = endOf(normalized.endDate, 'day');
    }
    if (normalized.entryDate) {
        normalized.entryDate = startOf(normalized.entryDate, 'day');
    }
    return normalized;
}

/**
 * The Pending Pledge Detail request body, shared by Show Results and Export to Excel so both cover exactly
 * the same pledges (whole-day date window, empty company = all companies).
 */
export function buildPendingPledgesDetailParams<T extends { companyId?: string | null; startDate?: ReportDate; endDate?: ReportDate }>(formValue: T): T {
    const model = normalizeReportDateRange(formValue);
    if (!model.companyId) {
        model.companyId = null;
    }
    return model;
}
